import React, { useMemo, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../../../components/ui/avatar';
import { ArrowLeft, Camera, Eye, EyeOff, Trash2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../../hooks/useApi';
import { toast } from 'react-toastify';
import { useAuth } from '../../../contexts/AuthContext';
import { isNativeIOSApp } from '../../../utils/platform';
import { compressImageFile, isImageFile } from '../../../utils/vehicleImage';


const API_BASE_URL = 'https://api.trackyourwheelz.com';
const FALLBACK_AVATAR = 'https://via.placeholder.com/400x400.png?text=Avatar';

const resolveAvatarUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  const normalizedBase = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
  const normalizedPath = url.startsWith('/') ? url : `/${url}`;
  return `${normalizedBase}${normalizedPath}`;
};

const LOCAL_PRESET_AVATARS = [
  '/engine-access-vehicle.png',
  '/engine-access-hero.png',
];

const ProfileSettings = () => {
  const navigate = useNavigate();
  const { put, post, get, del } = useApi();
  const { token, user, logout } = useAuth();
  const avatarInputRef = useRef(null);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [pendingAvatarFile, setPendingAvatarFile] = useState(null);

  const isNativeIos = isNativeIOSApp();

  const presetAvatars = useMemo(() => {
    const remotePresets = [
      'https://plus.unsplash.com/premium_photo-1739201499644-7878cbb4cdf9?q=80&w=1035&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      'https://plus.unsplash.com/premium_photo-1738449258706-74c1dc94b988?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1yZWxhdGVkfDE4fHx8ZW58MHx8fHx8',
      'https://plus.unsplash.com/premium_photo-1739088004528-bdaafb2f5f25?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1yZWxhdGVkfDQxfHx8ZW58MHx8fHx8',
      'https://media.istockphoto.com/id/1474386163/photo/young-professional-man-business-portrait-head-shirt-3d-rendering-character.webp?a=1&s=612x612&w=0&k=20&c=vezZTNAk1rFUVRj_5dbESI9h5jqegKN-A3FKr600h-4=',
    ];
    return isNativeIos ? LOCAL_PRESET_AVATARS : [...LOCAL_PRESET_AVATARS, ...remotePresets];
  }, [isNativeIos]);

  const securityHighlights = useMemo(() => [
    {
      title: 'Last Updated',
      value: '3 days ago',
      detail: 'Frequent changes keep your profile safer.'
    },
    {
      title: 'MFA Status',
      value: 'Active',
      detail: 'OTP is enforced for every admin login.'
    },
    {
      title: 'Device Trust',
      value: '2 devices',
      detail: 'Review trusted devices monthly.'
    }
  ], []);

  const quickTips = useMemo(() => [
    {
      title: 'Use passphrases',
      detail: 'Combine unrelated words with symbols for stronger passwords.'
    },
    {
      title: 'Rotate secrets',
      detail: 'Schedule reminders to refresh admin passwords quarterly.'
    },
    {
      title: 'Watch activity',
      detail: 'Monitor unusual login attempts in the security center.'
    }
  ], []);

  const [avatarPreview, setAvatarPreview] = useState(FALLBACK_AVATAR);
  const [avatarDisplayUrl, setAvatarDisplayUrl] = useState(FALLBACK_AVATAR);
  const [isAvatarDialogOpen, setIsAvatarDialogOpen] = useState(false);
  const [isAvatarLoaded, setIsAvatarLoaded] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await get('/user-profile');
        if (response?.success && response.data) {
          const apiAvatar = resolveAvatarUrl(response.data.avatarUrl);
          if (apiAvatar) {
            setAvatarPreview(apiAvatar);
            setPendingAvatarFile(null);
          } else {
            const random = presetAvatars[Math.floor(Math.random() * presetAvatars.length)];
            setAvatarPreview(random);
          }
        }
      } catch (error) {
      }
    };

    fetchProfile();
  }, [get, presetAvatars]);

  useEffect(() => {
    let objectUrl;
    const resolvedUrl =
      !avatarPreview
        ? ''
        : avatarPreview.startsWith('data:') || avatarPreview.startsWith('blob:')
          ? avatarPreview
          : avatarPreview.startsWith('http') || avatarPreview.startsWith('/')
            ? avatarPreview
            : `${API_BASE_URL}${avatarPreview.startsWith('/') ? avatarPreview : `/${avatarPreview}`}`;

    const requiresAuth =
      resolvedUrl && resolvedUrl.includes('/storage/avatars/') && !resolvedUrl.startsWith('data:') && !resolvedUrl.startsWith('blob:');

    const fetchProtectedAvatar = async () => {
      try {
        const response = await fetch(resolvedUrl, {
          method: 'POST',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` })
          }
        });
        if (!response.ok) {
          throw new Error('Failed to load avatar');
        }
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        setAvatarDisplayUrl(objectUrl);
      } catch (error) {
        setAvatarDisplayUrl(resolvedUrl);
      }
    };

    if (requiresAuth && token) {
      fetchProtectedAvatar();
    } else {
      setAvatarDisplayUrl(resolvedUrl);
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [avatarPreview, token]);

  const isPasswordFormDirty = useMemo(
    () => Object.values(formData).some(value => (value || '').trim() !== ''),
    [formData]
  );

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isImageFile(file)) {
      toast.error('Please select a valid image file.');
      return;
    }

    try {
      const compressed = await compressImageFile(file, {
        maxWidth: 512,
        maxHeight: 512,
        quality: 0.85,
      });
      const blobResponse = await fetch(compressed.dataUrl);
      const blob = await blobResponse.blob();
      const compressedFile = new File([blob], file.name || 'avatar.jpg', {
        type: 'image/jpeg',
      });
      setAvatarPreview(compressed.previewUrl);
      setPendingAvatarFile(compressedFile);
    } catch (error) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target.result);
      };
      reader.readAsDataURL(file);
      setPendingAvatarFile(file);
    }

    if (avatarInputRef.current) {
      avatarInputRef.current.value = '';
    }
  };

  const handlePresetSelect = async (url) => {
    setAvatarPreview(url);
    if (avatarInputRef.current) {
      avatarInputRef.current.value = '';
    }

    try {
      const response = await fetch(url);
      const blob = await response.blob();
      setPendingAvatarFile(new File([blob], 'avatar.jpg', { type: blob.type || 'image/jpeg' }));
    } catch {
      setPendingAvatarFile(null);
      toast.error('Could not load preset avatar. Please try another.');
    }
  };

  const openAvatarPicker = () => {
    avatarInputRef.current?.click();
  };
  const handleAvatarSave = async () => {
    if (!pendingAvatarFile) {
      toast.error('Please select an image before saving.');
      return;
    }

    const formDataToUpload = new FormData();
    formDataToUpload.append('avatar', pendingAvatarFile);

    try {
      setIsUploadingAvatar(true);
      const response = await post(`${API_BASE_URL}/api/user/avatar`, formDataToUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (response?.success) {
        const avatarUrl = response?.avatarUrl
          ? response.avatarUrl.startsWith('http')
            ? response.avatarUrl
            : `${API_BASE_URL}${response.avatarUrl}`
          : null;
        if (avatarUrl) {
          setAvatarPreview(avatarUrl);
        }
        toast.success(response?.message || 'Avatar updated successfully.');
        setPendingAvatarFile(null);
        const uploadInput = document.getElementById('avatar-upload');
        if (uploadInput) {
          uploadInput.value = '';
        }
      } else {
        toast.error(response?.message || 'Failed to update avatar.');
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to upload avatar. Please try again.');
    } finally {
      setIsUploadingAvatar(false);
    }
  };


  const openAvatarPreview = () => {
    setIsAvatarLoaded(false);
    setIsAvatarDialogOpen(true);
  };

  const closeAvatarPreview = () => {
    setIsAvatarDialogOpen(false);
    setIsAvatarLoaded(false);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordFormDirty) {
      return;
    }

    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      toast.error('Please fill in all password fields.');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('New password and confirm password must match.');
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        currentPassword: formData.currentPassword,
        password: formData.newPassword,
        confirmPassword: formData.confirmPassword
      };

      const response = await put(`${API_BASE_URL}/api/change-password`, payload, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (response?.success) {
        toast.success(response?.message || 'Password updated successfully.');
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        toast.error(response?.message || 'Unable to update password. Please try again.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeletingAccount(true);

      const role = (user?.role || '').toLowerCase();
      const isSuperAdmin = role === 'superadmin' || role === 'super_admin';

      let response = null;
      let lastError = null;

      const tryDelete = async (endpoint) => {
        try {
          return await del(endpoint);
        } catch (error) {
          lastError = error;
          return null;
        }
      };

      // Admin/customer users delete their own user profile — not the company account record.
      response =
        (await tryDelete('/user-profile')) ||
        (await tryDelete('/user/account')) ||
        (await tryDelete('/users/me'));

      if (!response) {
        try {
          response = await post('/user-profile/delete', {});
        } catch (error) {
          lastError = error;
        }
      }

      // Super admin fallback: delete linked company account when profile delete is unavailable.
      if (!response && isSuperAdmin) {
        const profileResponse = await get('/user-profile');
        const profileData = profileResponse?.success ? profileResponse.data : profileResponse;
        const accountId =
          profileData?.accountId ||
          profileData?.account_id ||
          profileData?.account?.id ||
          profileData?.account?._id;

        if (accountId) {
          response = await tryDelete(`/accounts/${accountId}`);
        }
      }

      if (!response) {
        throw lastError || new Error('Failed to delete account. Please contact support.');
      }

      toast.success(response?.message || 'Your account has been deleted.');
      setIsDeleteConfirmOpen(false);
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error(error?.message || 'Failed to delete account. Please try again.');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-2 app-page md:py-6 lg:py-12">
      <div className="w-full max-w-3xl mx-auto lg:max-w-none lg:w-full">
        <button className='flex items-center gap-2 text-foreground lg:hidden mb-3' onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <Card className="border-0 shadow-md bg-muted/50 lg:border lg:border-border/50 lg:bg-gradient-to-br lg:from-muted/70 lg:via-background lg:to-background lg:shadow-2xl lg:rounded-[28px]">
          <CardHeader className="pb-0 lg:pb-2">
            <CardTitle className="text-xl font-semibold lg:text-2xl lg:flex lg:items-center lg:justify-between">
              Settings
              <span className="hidden lg:inline-flex text-sm font-normal text-muted-foreground">
                Tailored desktop controls · Live previews
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-8 lg:grid lg:grid-cols-[320px_1fr] lg:gap-8 lg:space-y-0">
              <div className="flex flex-col items-center gap-4 sm:gap-6 lg:items-stretch lg:bg-background/60 lg:rounded-3xl lg:border lg:border-white/10 lg:p-8 lg:shadow-lg lg:backdrop-blur">
                <div className="relative">
                  <button
                    type="button"
                    onClick={openAvatarPreview}
                    className=" rounded-full"
                    aria-label="Preview profile avatar"
                  >
                    <Avatar className="w-28 h-28 sm:w-32 sm:h-32 border-1 border-muted-foreground/20 shadow-lg overflow-hidden lg:w-40 lg:h-40 lg:border-primary/20 lg:shadow-2xl">
                      <AvatarImage
                        src={avatarDisplayUrl}
                        alt="Current avatar"
                        className="w-full h-full object-cover"
                      />
                      <AvatarFallback className="text-2xl">AV</AvatarFallback>
                    </Avatar>
                  </button>
                  <button
                    type="button"
                    onClick={openAvatarPicker}
                    className="absolute bottom-2 right-2 inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground shadow hover:opacity-90 transition-opacity lg:w-12 lg:h-12"
                    aria-label="Change profile photo"
                  >
                    <Camera className="w-4 h-4 lg:w-5 lg:h-5" />
                  </button>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    id="avatar-upload"
                    className="hidden"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                    onChange={handleAvatarChange}
                  />
                </div>

                <Button
                  className="w-full text-foreground sm:hidden"
                  variant="outline"
                  type="button"
                  onClick={openAvatarPicker}
                >
                  Change Photo
                </Button>

                {isNativeIos ? (
                  <p className="text-xs text-center text-muted-foreground px-2">
                    Choose from photo library or pick a preset below. Avoid camera capture in the iOS app.
                  </p>
                ) : null}

                <Button
                  className='w-full text-foreground'
                  variant="outline"
                  type="button"
                  onClick={handleAvatarSave}
                  disabled={isUploadingAvatar || !pendingAvatarFile}
                >
                  {isUploadingAvatar ? 'Saving...' : 'Save Avatar'}
                </Button>

                <div className="w-full max-w-sm mx-auto lg:max-w-none">
                  <h3 className="text-sm font-semibold text-foreground/80 mb-3">Preset avatars</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {presetAvatars.map((avatar, index) => (
                      <button
                        key={avatar}
                        type="button"
                        onClick={() => handlePresetSelect(avatar)}
                        className={`group relative overflow-hidden rounded-2xl border p-0 transition-all duration-200 ${
                          avatarPreview === avatar ? 'border-primary ring-2 ring-primary/40' : 'border-border/60 hover:border-primary/40'
                        }`}
                      >
                        <img src={avatar} alt={`Preset ${index + 1}`} className="h-24 w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <form className="space-y-4 sm:space-y-6 lg:p-8 lg:bg-background/60 lg:border lg:border-white/10 lg:rounded-3xl lg:shadow-lg lg:backdrop-blur" onSubmit={handlePasswordSubmit}>
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="current-password"
                      type={showPasswords.current ? 'text' : 'password'}
                      placeholder="Enter current password"
                      value={formData.currentPassword}
                      onChange={(e) => handleChange('currentPassword', e.target.value)}
                      className="border-0 bg-background text-sm pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('current')}
                      className="absolute inset-y-0 right-3 flex items-center text-muted-foreground hover:text-foreground"
                    >
                      {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPasswords.new ? 'text' : 'password'}
                      placeholder="Enter new password"
                      value={formData.newPassword}
                      onChange={(e) => handleChange('newPassword', e.target.value)}
                      className="border-0 bg-background text-sm pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('new')}
                      className="absolute inset-y-0 right-3 flex items-center text-muted-foreground hover:text-foreground"
                    >
                      {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showPasswords.confirm ? 'text' : 'password'}
                      placeholder="Re-enter new password"
                      value={formData.confirmPassword}
                      onChange={(e) => handleChange('confirmPassword', e.target.value)}
                      className="border-0 bg-background text-sm pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('confirm')}
                      className="absolute inset-y-0 right-3 flex items-center text-muted-foreground hover:text-foreground"
                    >
                      {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="hidden lg:grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                  {securityHighlights.map(item => (
                    <div key={item.title} className="rounded-2xl border-1 border-muted-foreground/20 bg-muted/50  from-muted/40 via-background to-background p-4 shadow-sm">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground">{item.title}</p>
                      <p className="text-xl font-semibold text-foreground mt-1">{item.value}</p>
                      <p className="text-xs text-muted-foreground mt-2">{item.detail}</p>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end lg:border-t lg:border-border/60 lg:pt-6">
                  <Button
                    variant="outline"
                    className='border-1 border-muted-foreground/20 w-full sm:w-auto lg:px-10 lg:py-6 lg:text-sm lg:font-semibold'
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6 border border-red-200/60 bg-red-50/40 shadow-md dark:border-red-900/40 dark:bg-red-950/20 lg:rounded-[28px]">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-red-700 dark:text-red-400">Delete Account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Permanently delete your account and associated personal data. This action cannot be undone.
            </p>
            <Button
              type="button"
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950/40"
              onClick={() => setIsDeleteConfirmOpen(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Account
            </Button>
          </CardContent>
        </Card>
      </div>

      {isDeleteConfirmOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
            <Card className="w-full max-w-md md:max-w-lg">
              <CardContent className="p-4 space-y-4">
                <div>
                  <p className="text-base font-semibold text-foreground">Delete Account</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Are you sure you want to delete your account?
                  </p>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsDeleteConfirmOpen(false)}
                    disabled={isDeletingAccount}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="bg-red-600 text-white hover:bg-red-700"
                    onClick={handleDeleteAccount}
                    disabled={isDeletingAccount}
                  >
                    {isDeletingAccount ? 'Deleting...' : 'Delete Permanently'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>,
          document.body
        )}

      {isAvatarDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6 animate-in fade-in-0">
          <div className="relative max-h-[90vh] w-full max-w-xl overflow-hidden rounded-2xl border-1 border-muted-foreground/20 bg-card p-4 shadow-2xl animate-in fade-in-0 zoom-in-95">
            <button
              type="button"
              onClick={closeAvatarPreview}
              className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-background/80 text-foreground shadow hover:bg-background focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring"
              aria-label="Close avatar preview"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex flex-col items-center gap-3 pt-2">
              <div className="max-h-[70vh] w-full overflow-auto rounded-lg bg-muted/40 p-2">
                <img
                  src={avatarPreview}
                  alt="Profile avatar preview"
                  className={`mx-auto max-h-[65vh] w-full object-contain transition-all duration-300 ease-out ${
                    isAvatarLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                  }`}
                  onLoad={() => setIsAvatarLoaded(true)}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = 'https://via.placeholder.com/400x400.png?text=Avatar';
                  }}
                />
              </div>
              <p className="text-sm text-muted-foreground">Current profile photo</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSettings;